// Typed fetch wrapper for the GC Coach API.
//
// Injects the Bearer id_token from sessionStorage on every request unless
// the caller opts out (`auth: false` for public routes like /health).
//
// id_token (not access_token): Cognito access tokens omit `email` and any
// custom attributes. The API's JWT verifier needs `email` (used in /auth/me
// + most authed handlers via requireAuth → Mongo lazy-upsert). id_token
// carries the full identity claim set (sub, email, email_verified) and
// passes the same jose verification path; verifyToken's `token_use` switch
// accepts either flavor. Switching to access_token requires either dropping
// the email requirement backend-side or wiring a Cognito PreTokenGeneration
// trigger to enrich access tokens. id_token is the smallest-diff choice.
//
// Returns a discriminated union { ok: true, data } | { ok: false, error }
// rather than throwing — keeps screen code free of try/catch noise.
//
// On 401 from an authed call, invokes the registered onUnauthorized callback
// (wired by AuthProvider to the useAuthFlow.flagSessionExpired intent). The
// API client itself stays React-agnostic.

import { env } from '../config/env.js';
import { loadTokens } from '../auth/tokenStorage.js';

let onUnauthorizedCallback: (() => void) | null = null;

/**
 * Register a callback invoked when an authed request returns 401. Wired once
 * at AuthProvider mount.
 */
export function setOnUnauthorized(callback: (() => void) | null): void {
  onUnauthorizedCallback = callback;
}

export interface ApiOk<T> {
  ok: true;
  status: number;
  data: T;
}

export interface ApiErr {
  ok: false;
  status: number;
  /** Best-effort short code for the screen to branch on. */
  code:
    | 'UNAUTHORIZED'
    | 'FORBIDDEN'
    | 'NOT_FOUND'
    | 'BAD_REQUEST'
    | 'VALIDATION_FAILED'
    | 'RATE_LIMITED'
    | 'NETWORK'
    | 'SERVER'
    | 'UNKNOWN';
  message: string;
  /** Server requestId if the response carried one — useful for support tickets. */
  requestId?: string;
}

export type ApiResult<T> = ApiOk<T> | ApiErr;

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Set false for public endpoints (e.g. /health). Defaults to true. */
  auth?: boolean;
  /** JSON body; will be JSON.stringify'd and Content-Type set. */
  body?: unknown;
  /** Extra headers to merge in. */
  headers?: Record<string, string>;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: ApiRequest = {}): Promise<ApiResult<T>> {
  const { method = 'GET', auth = true, body, headers = {}, signal } = opts;

  const url = `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const finalHeaders: Record<string, string> = { Accept: 'application/json', ...headers };

  if (auth) {
    const tokens = loadTokens();
    if (!tokens) {
      return {
        ok: false,
        status: 0,
        code: 'UNAUTHORIZED',
        message: 'No active session',
      };
    }
    finalHeaders.Authorization = `Bearer ${tokens.idToken}`;
  }

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    const init: RequestInit = { method, headers: finalHeaders };
    if (payload !== undefined) init.body = payload;
    if (signal) init.signal = signal;
    response = await fetch(url, init);
  } catch (err) {
    return {
      ok: false,
      status: 0,
      code: 'NETWORK',
      message: (err as Error).message || 'Network error',
    };
  }

  const requestId =
    response.headers.get('x-amzn-requestid') ??
    response.headers.get('x-request-id') ??
    undefined;

  if (response.status === 204) {
    return { ok: true, status: 204, data: undefined as T };
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // Non-JSON body — leave parsed as null.
  }

  if (response.ok) {
    return { ok: true, status: response.status, data: parsed as T };
  }

  const errBody = (parsed as { code?: string; message?: string } | null) ?? {};

  // Notify the auth layer when an authed call comes back 401 — almost always
  // means the token expired (or was revoked). The callback typically routes
  // the user into the session-expired modal. Public calls (auth=false) don't
  // trigger this; their 401s are surface-level errors.
  if (response.status === 401 && auth && onUnauthorizedCallback) {
    onUnauthorizedCallback();
  }

  const code: ApiErr['code'] =
    response.status === 401
      ? 'UNAUTHORIZED'
      : response.status === 403
        ? 'FORBIDDEN'
        : response.status === 404
          ? 'NOT_FOUND'
          : response.status === 400
            ? 'BAD_REQUEST'
            : response.status === 422
              ? 'VALIDATION_FAILED'
              : response.status === 429
                ? 'RATE_LIMITED'
                : response.status >= 500
                  ? 'SERVER'
                  : 'UNKNOWN';
  const out: ApiErr = {
    ok: false,
    status: response.status,
    code,
    message: errBody.message ?? response.statusText ?? 'Request failed',
  };
  if (requestId) out.requestId = requestId;
  return out;
}
